import { z } from 'zod';
import { clampLayout, defaultLayout, LAYOUT_LIMITS, type LayoutKey } from './layout.ts';

// Het API-contract van Jerome 2: schema's die server en client delen. Alles wat over de lijn
// gaat (REST en websocket) is hier beschreven; de server valideert invoer ermee, de client
// gebruikt de afgeleide types.

// ---- Mail ----

export const MailAddress = z.object({ name: z.string(), address: z.string() });
export type MailAddress = z.infer<typeof MailAddress>;

export const WellKnownFolder = z.enum(['inbox', 'drafts', 'sentitems', 'deleteditems', 'archive', 'junkemail']);
export type WellKnownFolder = z.infer<typeof WellKnownFolder>;

export const Folder = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  parentId: z.string().nullable(),
  wellKnown: WellKnownFolder.nullable(),
  unread: z.number().int(),
  flagged: z.number().int(),
  total: z.number().int(),
  childCount: z.number().int(),
});
export type Folder = z.infer<typeof Folder>;

export const FlagStatus = z.enum(['notFlagged', 'flagged', 'complete']);
export const LastVerb = z.enum(['reply', 'replyAll', 'forward']).nullable();

export const TaskStatus = z.enum(['queued', 'running', 'done', 'question', 'failed', 'finished', 'cancelled']);
export type TaskStatus = z.infer<typeof TaskStatus>;
export const TaskKind = z.enum(['reply', 'summary', 'sign', 'research', 'agenda', 'knowledge', 'other']);
export type TaskKind = z.infer<typeof TaskKind>;
export const TaskSource = z.enum(['text', 'voice', 'auto', 'steer', 'answer']);
export type TaskSource = z.infer<typeof TaskSource>;

/** Korte taakinformatie zoals die bij een mail in een lijst hoort. */
export const TaskSummary = z.object({
  id: z.string(),
  status: TaskStatus,
  kind: TaskKind,
  instruction: z.string(),
  progress: z.string().nullable(),
  resultType: z.string().nullable(),
  updatedAt: z.string(),
  /** Deel van een gesplitste instructie (meerdere acties in één instructie): welk deel en hoeveel er zijn. */
  split: z.object({ index: z.number().int(), total: z.number().int() }).nullable().optional(),
});
export type TaskSummary = z.infer<typeof TaskSummary>;

export const MailItem = z.object({
  id: z.string(),
  subject: z.string(),
  from: MailAddress.nullable(),
  to: z.array(MailAddress),
  received: z.string().nullable(),
  modified: z.string().nullable(),
  preview: z.string(),
  isRead: z.boolean(),
  isDraft: z.boolean(),
  flagged: z.boolean(),
  flagStatus: FlagStatus,
  flagDue: z.string().nullable(),
  hasAttachments: z.boolean(),
  importance: z.enum(['low', 'normal', 'high']),
  webLink: z.string().nullable(),
  conversationId: z.string().nullable(),
  parentFolderId: z.string().nullable(),
  lastVerb: LastVerb,
  lastVerbAt: z.string().nullable(),
});
export type MailItem = z.infer<typeof MailItem>;

export const MailListItem = MailItem.extend({
  urgency: z.number().int().min(1).max(5).nullable(),
  urgencyReason: z.string().nullable(),
  task: TaskSummary.nullable(),
  /** Dag (JJJJ-MM-DD) waarvoor deze mail in de einde-dag-samenvatting zit; anders leeg. */
  eodDay: z.string().nullable().optional(),
});
export type MailListItem = z.infer<typeof MailListItem>;

export const MailAttachment = z.object({
  id: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string().nullable(),
  isInline: z.boolean(),
  contentId: z.string().nullable(),
});
export type MailAttachment = z.infer<typeof MailAttachment>;

export const MailThreadItem = MailItem.extend({ fromMe: z.boolean() });
export type MailThreadItem = z.infer<typeof MailThreadItem>;

export const MailDetail = MailListItem.extend({
  cc: z.array(MailAddress),
  bodyHtml: z.string().nullable(),
  bodyText: z.string(),
  attachments: z.array(MailAttachment),
  thread: z.object({ later: z.array(MailThreadItem) }),
});
export type MailDetail = z.infer<typeof MailDetail>;

export const MailView = z.enum(['flagged', 'unread', 'processed', 'search', 'folder']);
export type MailView = z.infer<typeof MailView>;
export const MailSort = z.enum(['newest', 'oldest', 'urgent']);
export type MailSort = z.infer<typeof MailSort>;
export const ProcessedFilter = z.enum(['all', 'done', 'question', 'running', 'failed']);
export type ProcessedFilter = z.infer<typeof ProcessedFilter>;

export const MailListQuery = z.object({
  view: MailView.default('flagged'),
  folder: z.string().default('all'),
  q: z.string().optional(),
  sort: MailSort.default('newest'),
  processed: ProcessedFilter.default('all'),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  skip: z.coerce.number().int().min(0).default(0),
});
export type MailListQuery = z.infer<typeof MailListQuery>;

export const MailListResponse = z.object({ items: z.array(MailListItem), more: z.boolean() });
export type MailListResponse = z.infer<typeof MailListResponse>;

export const MailAction = z.enum(['archive', 'delete', 'flag', 'unflag', 'read', 'unread', 'move']);
export type MailAction = z.infer<typeof MailAction>;
export const MailActionBody = z.object({ action: MailAction, folderId: z.string().optional() });
export type MailActionBody = z.infer<typeof MailActionBody>;

export const InstructionBody = z.object({ text: z.string().trim().min(1).max(4000), source: z.enum(['text', 'voice']).default('text') });
export type InstructionBody = z.infer<typeof InstructionBody>;

export const Counts = z.object({
  flagged: z.number().int(),
  unread: z.number().int(),
  done: z.number().int(),
  question: z.number().int(),
  running: z.number().int(),
  failed: z.number().int(),
});
export type Counts = z.infer<typeof Counts>;

// ---- Concepten ----

export const DraftMode = z.enum(['new', 'reply', 'replyAll', 'forward']);
export type DraftMode = z.infer<typeof DraftMode>;

export const DraftBody = z.object({
  mode: DraftMode,
  messageId: z.string().optional(),
  to: z.array(z.string()).default([]),
  cc: z.array(z.string()).default([]),
  subject: z.string().default(''),
  bodyText: z.string().default(''),
});
export type DraftBody = z.infer<typeof DraftBody>;

export const DraftPatch = z.object({
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  subject: z.string().optional(),
  bodyText: z.string().optional(),
});
export type DraftPatch = z.infer<typeof DraftPatch>;

export const Draft = z.object({
  id: z.string(),
  subject: z.string(),
  to: z.array(MailAddress),
  cc: z.array(MailAddress),
  bodyText: z.string(),
  bodyHtml: z.string().nullable(),
  isDraft: z.boolean(),
  taskId: z.string().nullable(),
  messageId: z.string().nullable(),
  /** Hoe het concept ontstond; 'new' als Jerome het concept niet kent (bijvoorbeeld in Outlook gemaakt). */
  mode: DraftMode,
  /** De tekst zoals Jerome die schreef; null als Jeroen het concept zelf begon. Na versturen leert Jerome van het verschil. */
  originalText: z.string().nullable(),
});
export type Draft = z.infer<typeof Draft>;

/** Wat de connector met het huidige Microsoft-token mag; send = null als dat niet bekend is. */
// Wat de server kan: versturen (Mail.Send bij de connector; null = onbekend) en spraak (dicteren en voorlezen
// via ElevenLabs; allebei alleen met een sleutel). `voice` is optioneel zodat een oudere server tijdens een deploy
// nog past; de client neemt dan aan dat spraak kan.
export const VoiceCapabilities = z.object({ dictation: z.boolean(), speech: z.boolean() });
export type VoiceCapabilities = z.infer<typeof VoiceCapabilities>;
export const Capabilities = z.object({ send: z.boolean().nullable(), voice: VoiceCapabilities.optional() });
export type Capabilities = z.infer<typeof Capabilities>;

// ---- WhatsApp (alleen-lezen koppeling, sinds 2.3.0; koppelen en vastleggen sinds 2.3.1) ----
// De stand van de koppeling staat in de tabel wa_account (één rij), die de Go-dienst jerome-whatsapp bijhoudt (map
// whatsapp/ in deze repo). Jerome 2 leest hem en geeft de dienst opdrachten via de tabel wa_commands (koppelen met een code of
// een QR-code, ontkoppelen, opnieuw verbinden); de database is de enige weg tussen de twee. De uitleg van de foutcodes in gewone
// taal en het nummer zoals de dienst het wil, staan in whatsapp.ts.
export const WaState = z.enum(['not_linked', 'linking', 'linked', 'logged_out', 'banned']);
export type WaState = z.infer<typeof WaState>;
/** Zo lang geldt een hartslag van de dienst als teken van leven: hij schrijft er elke 15 s een, dus drie gemist is stil. */
export const WA_ALIVE_MS = 45_000;
/** De opdrachten die de dienst kent (kolom kind van wa_commands). Jerome 2 geeft in 2.3.1 alleen koppelcode, qr, ontkoppelen en
 * verbinden; media_ophalen en geschiedenis_aanvullen zet de dienst in 2.3.1 op mislukt ('komt in een latere versie'). */
export const WaCommandKind = z.enum(['koppelcode', 'qr', 'ontkoppelen', 'verbinden', 'media_ophalen', 'geschiedenis_aanvullen']);
export type WaCommandKind = z.infer<typeof WaCommandKind>;
/** De stand van een opdracht: wachtrij (standaard), bezig, klaar of mislukt (met een korte reden zonder inhoud in result). */
export const WaCommandState = z.enum(['wachtrij', 'bezig', 'klaar', 'mislukt']);
export type WaCommandState = z.infer<typeof WaCommandState>;
/** De opdrachten die nog lopen. */
export const WA_OPEN_COMMAND_STATES: readonly WaCommandState[] = ['wachtrij', 'bezig'];
/** De laatste opdracht zoals de client hem ziet: soort en stand als tekst (een soort die deze client niet kent, breekt niets). */
export const WaCommandView = z.object({
  id: z.string(),
  kind: z.string(),
  state: z.string(),
  result: z.string(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
});
export type WaCommandView = z.infer<typeof WaCommandView>;
export const WaHistory = z.object({ chunks: z.number(), messages: z.number(), progress: z.number(), lastAt: z.string().nullable(), done: z.boolean() });
export type WaHistory = z.infer<typeof WaHistory>;
/** De bestanden uit WhatsApp per stand (sinds 2.3.5, uit wa_media_stats): aantallen, en van de opgehaalde ook de grootte in de opslag (een
 * bestand dat in meer berichten staat, telt daar één keer). Alleen getallen. */
export const WaMediaCounts = z.object({
  /** Opgehaald: in de bucket, te openen via de route. */
  stored: z.number(),
  storedBytes: z.number(),
  /** In de wachtrij van de dienst. */
  queued: z.number(),
  /** Niet meer bij WhatsApp; op verzoek vraagt de dienst het aan de telefoon. */
  expired: z.number(),
  /** Groter dan 25 MB: alleen op verzoek, tot 100 MB. */
  tooLarge: z.number(),
  /** Aan de telefoon gevraagd, wacht op het antwoord. */
  requested: z.number(),
  /** Vijf keer mislukt. */
  failed: z.number(),
});
export type WaMediaCounts = z.infer<typeof WaMediaCounts>;
/** Body van POST /api/whatsapp/koppelen en /api/whatsapp/qr: het eigen nummer zoals de eigenaar het typt (de server haalt spaties,
 * plustekens, streepjes en haakjes weg en toetst het met waNumberOf). */
export const WaPairBody = z.object({ nummer: z.string().max(40) });
export type WaPairBody = z.infer<typeof WaPairBody>;
/** Antwoord van de vier POST-routes: de id van de opdracht in wa_commands (202). */
export const WaCommandCreated = z.object({ id: z.string() });
export type WaCommandCreated = z.infer<typeof WaCommandCreated>;
/** Een melding over WhatsApp (in de app en per mail, server/src/watch/notify.ts): ontkoppeld, geblokkeerd of gestopt, en sinds 2.3.6 een
 * telefoon die tien dagen (phone_quiet) of dertien dagen (phone_remind) niet online was. Zonder inhoud, nummer, JID of koppelcode; `text`
 * is de uitleg in gewone taal. */
export const WaAlertKind = z.enum(['logged_out', 'banned', 'client_outdated', 'stream_replaced', 'connect_failed', 'phone_quiet', 'phone_remind']);
export type WaAlertKind = z.infer<typeof WaAlertKind>;
export const WaAlert = z.object({ kind: WaAlertKind, title: z.string(), text: z.string(), at: z.string() });
export type WaAlert = z.infer<typeof WaAlert>;
export const WaStatus = z.object({
  state: WaState,
  /** Of de koppeling actief is (state === 'linked'). */
  linked: z.boolean(),
  /** Of de dienst draait: er is een houder en de laatste hartslag is hooguit WA_ALIVE_MS oud, gemeten op de klok van de
   * server. Een dienst die netjes stopt, maakt de houder leeg en laat de laatste hartslag staan. */
  alive: z.boolean(),
  /** De instantie van de dienst die het slot houdt; leeg als er geen draait of als hij netjes stopte. */
  holder: z.string(),
  /** Laatste hartslag van de dienst (ISO) of null. */
  heartbeatAt: z.string().nullable(),
  /** De versie van whatsmeow waarmee de dienst draait (de pseudo-versie uit go.mod). */
  libVersion: z.string(),
  /** Laatste foutcode van de koppeling, leeg als er geen is (alleen een code, nooit tekst met gegevens). */
  lastError: z.string(),
  updatedAt: z.string().nullable(),
  // Sinds 2.3.1 (koppelen en vastleggen). Nooit de eigen JID, LID of het apparaatnummer: die blijven in de database.
  /** Het eigen nummer zoals de eigenaar het invulde (alleen cijfers), '' als onbekend. Alleen voor het veld op het scherm; nooit in
   * een logregel, melding of mail. */
  ownNumber: z.string(),
  /** Gekoppeld sinds (ISO) of null. */
  linkedAt: z.string().nullable(),
  /** Er is nu een verbinding met WhatsApp. */
  connected: z.boolean(),
  /** Laatste geslaagde verbinding (ISO) of null. */
  connectedAt: z.string().nullable(),
  /** Laatste teken van de telefoon (een bericht van de eigen telefoon) of null. */
  phoneSeenAt: z.string().nullable(),
  /** De koppelcode `XXXX-XXXX` tijdens het koppelen met een code; '' als er geen is of als hij verlopen is (klok van de server). */
  pairCode: z.string(),
  pairCodeExpiresAt: z.string().nullable(),
  /** De QR-tekst tijdens het koppelen met een QR-code (de client tekent hem); '' als er geen is of als hij verlopen is. */
  qrCode: z.string(),
  qrExpiresAt: z.string().nullable(),
  /** De geschiedenis die de telefoon na het koppelen stuurt: delen, berichten, voortgang (0 tot 100), laatste deel en klaar. */
  history: WaHistory,
  /** Moment van `lastError` (ISO) of null. */
  lastErrorAt: z.string().nullable(),
  /** `lastError` in gewone taal (waErrorText in whatsapp.ts); '' zonder fout. */
  lastErrorText: z.string(),
  /** Stopcode: niet leeg = de dienst is gestopt en verbindt pas weer op de opdracht verbinden (knop Opnieuw verbinden). */
  halted: z.string(),
  haltedText: z.string(),
  /** Aantal afgevangen panieken in de gebeurtenisverwerking van de dienst. */
  panics: z.number(),
  /** Tellingen van het archief (de dienst houdt ze bij in wa_account). */
  counts: z.object({ chats: z.number(), messages: z.number() }),
  /** Het laatste bericht in het archief (ISO) of null (sinds 2.3.3): uit de archieftabellen zelf, zoals de tool whatsapp_status het
   * noemt (wa_archive_stats, zonder gewiste gesprekken). Alleen een moment, nooit inhoud of een gesprek. */
  lastMessageAt: z.string().nullable(),
  /** De bestanden per stand (sinds 2.3.5, de rij Bestanden), of null als ze niet te lezen zijn (blok 0009 nog niet toegepast). */
  media: WaMediaCounts.nullable(),
  /** De laatste opdracht aan de dienst om te koppelen, te ontkoppelen of te verbinden, zodat het scherm "wacht op de dienst" kan tonen; null
   * als er nog geen is. Sinds 2.3.5 telt media_ophalen niet mee: die opdrachten komen uit de chat en lopen naast de koppeling. */
  command: WaCommandView.nullable(),
});
export type WaStatus = z.infer<typeof WaStatus>;
/** Hoe lang een opdracht aan de dienst mag wachten voordat het scherm zegt dat de dienst haar niet oppakt. */
export const WA_COMMAND_STALE_MS = 30_000;
export const WA_STATE_LABELS: Record<WaState, string> = {
  not_linked: 'Niet gekoppeld',
  linking: 'Koppelen…',
  linked: 'Gekoppeld',
  logged_out: 'Ontkoppeld',
  banned: 'Geblokkeerd',
};

// ---- Taken ----

export const EventProposal = z.object({
  subject: z.string(),
  start: z.string(),
  end: z.string(),
  isAllDay: z.boolean().optional(),
  location: z.string().optional(),
  attendees: z.array(z.string()).optional(),
  body: z.string().optional(),
  online: z.boolean().optional(),
});
export type EventProposal = z.infer<typeof EventProposal>;

export const SignFile = z.object({
  name: z.string(),
  path: z.string(),
  pages: z.number().int(),
  draftAttachmentId: z.string().optional(),
  /** OneDrive-paden van paginabeelden (via GET /api/sign/thumb?path=). */
  thumbnails: z.array(z.string()).optional(),
});

export const SignOutcome = z.object({
  files: z.array(SignFile),
  score: z.number(),
  verdict: z.string(),
  findings: z.array(z.string()),
  rounds: z.number().int(),
  rulesUsed: z.array(z.string()),
});
export type SignOutcome = z.infer<typeof SignOutcome>;

export const ResultType = z.enum(['reply', 'summary', 'research', 'agenda', 'sign', 'knowledge', 'action', 'nothing', 'other']);
export type ResultType = z.infer<typeof ResultType>;

export const TaskResult = z.object({
  type: ResultType,
  title: z.string(),
  text: z.string(),
  draftId: z.string().optional(),
  draftMode: DraftMode.optional(),
  attachments: z.array(z.object({ name: z.string(), path: z.string() })).optional(),
  event: EventProposal.optional(),
  sources: z.array(z.object({ title: z.string(), url: z.string().optional(), ref: z.string().optional() })).optional(),
  sign: SignOutcome.optional(),
  notes: z.string().optional(),
});
export type TaskResult = z.infer<typeof TaskResult>;

export const Task = z.object({
  id: z.string(),
  messageId: z.string(),
  conversationId: z.string().nullable(),
  folderId: z.string().nullable(),
  subject: z.string(),
  fromName: z.string().nullable(),
  fromAddress: z.string().nullable(),
  receivedAt: z.string().nullable(),
  instruction: z.string(),
  source: TaskSource,
  kind: TaskKind,
  status: TaskStatus,
  priority: z.number().int(),
  attempt: z.number().int(),
  model: z.string().nullable(),
  effort: z.string().nullable(),
  sdkSessionId: z.string().nullable(),
  workerId: z.string().nullable(),
  heartbeatAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  costUsd: z.number(),
  result: TaskResult.nullable(),
  artifacts: z.record(z.string(), z.unknown()),
  question: z.string().nullable(),
  answer: z.string().nullable(),
  error: z.string().nullable(),
  progress: z.string().nullable(),
  parentTaskId: z.string().nullable(),
  /** Wanneer de eigenaar het resultaat op het scherm opende (2.3.10, blok 0012-werkers-bekeken); null tot dan, en weer null als
   * de taak terug in de wachtrij gaat. De werkerskolom zet een klare taak daarmee van "Klaar, nog niet bekeken" naar "Klaar en bekeken". */
  seenAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Task = z.infer<typeof Task>;

export const TaskEventKind = z.enum(['status', 'progress', 'tool', 'result', 'question', 'error', 'note']);
export type TaskEventKind = z.infer<typeof TaskEventKind>;

export const TaskEvent = z.object({
  id: z.string(),
  taskId: z.string(),
  seq: z.number().int(),
  at: z.string(),
  kind: TaskEventKind,
  text: z.string(),
  data: z.record(z.string(), z.unknown()).nullable(),
});
export type TaskEvent = z.infer<typeof TaskEvent>;

export const TaskTextBody = z.object({ text: z.string().trim().min(1).max(4000) });

// ---- Chat ----

export const ChatKind = z.enum(['chat', 'agenda', 'dev', 'briefing', 'rules']);
export type ChatKind = z.infer<typeof ChatKind>;
export const ModelTierSchema = z.enum(['standaard', 'zwaar', 'licht']);

export const Chat = z.object({
  id: z.string(),
  kind: ChatKind,
  title: z.string(),
  status: z.enum(['idle', 'running', 'error']),
  modelTier: ModelTierSchema,
  fast: z.boolean(),
  sdkSessionId: z.string().nullable(),
  context: z.record(z.string(), z.unknown()).nullable(),
  pinned: z.boolean(),
  archived: z.boolean(),
  totalCostUsd: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastMessageAt: z.string().nullable(),
});
export type Chat = z.infer<typeof Chat>;

export const MessageKind = z.enum(['text', 'tool_call', 'tool_result', 'status', 'error', 'proposal', 'permission']);
/** Een bericht in een gesprek. `content` is vrij per soort; bij een bericht van Jeroen betekent `content.queued === true`
 * dat het in de wachtrij staat: het wacht tot Jerome klaar is met de lopende beurt (zie isQueuedMessage). */
export const Message = z.object({
  id: z.string(),
  chatId: z.string(),
  seq: z.number().int(),
  role: z.enum(['user', 'assistant', 'system']),
  kind: MessageKind,
  content: z.record(z.string(), z.unknown()),
  model: z.string().nullable(),
  usage: z.record(z.string(), z.unknown()).nullable(),
  costUsd: z.number().nullable(),
  createdAt: z.string(),
});
export type Message = z.infer<typeof Message>;
/** Een bericht van Jeroen dat wacht tot Jerome klaar is met de lopende beurt. */
export const isQueuedMessage = (m: Pick<Message, 'role' | 'content'>): boolean => m.role === 'user' && m.content['queued'] === true;

/** Een mail die Jerome in een gesprek vond of las: wat het mailpaneel van de chat nodig heeft voor een rij (bericht van soort
 * tool_result, `content.mails`); de mail zelf haalt de client op via GET /api/mail/:id. */
export const ChatMailRef = z.object({
  id: z.string(),
  subject: z.string(),
  from: MailAddress.nullable(),
  received: z.string().nullable(),
  unread: z.boolean(),
  flagged: z.boolean(),
  hasAttachments: z.boolean(),
});
export type ChatMailRef = z.infer<typeof ChatMailRef>;
/** De mails in een bericht van soort tool_result; leeg voor elk ander bericht of een inhoud die niet klopt. */
export function chatMailRefs(m: Pick<Message, 'kind' | 'content'>): ChatMailRef[] {
  if (m.kind !== 'tool_result') return [];
  const r = z.array(ChatMailRef).safeParse(m.content['mails']);
  return r.success ? r.data : [];
}

/** Grenzen voor een nieuw gesprek (ticket 30961): de titel, en elke tekst in de context. De context van een gesprek over een mail
 * draagt het onderwerp en de afzender van die mail, en die komen van buiten; zonder grens kon alleen de omvang van het verzoek ze
 * tegenhouden. De client blijft eronder (`mailChatBody` in client/src/state/mailTabs.ts). */
export const CHAT_TITLE_MAX = 200;
export const CHAT_CONTEXT_TEXT_MAX = 2000;
/** Een id van een mail of afspraak zoals de server het in een prompt zet (`idText` in server/src/agent/data.ts): 1 tot 300 tekens, alleen
 * letters, cijfers en `_ = + / . -` (2.2.54). Een tekst in plaats van een id geeft 400 en komt zo nooit in een prompt. */
export const ITEM_ID_PATTERN = /^[\w=+/.-]{1,300}$/;
export const ItemId = z.string().regex(ITEM_ID_PATTERN, 'Dit is geen geldig id.');
/** Een tijd in ISO 8601, met `Z`, een verschuiving of zonder zone (2.2.54): de periode van een agendagesprek. */
export const IsoTime = z.iso.datetime({ offset: true, local: true });
/** De context van een agendagesprek (2.2.54): alleen de periode in beeld (`from` en `to`, elk een tijd) en de afspraak van Bespreek
 * (`eventId`, een id). Een andere sleutel, of een tekst in plaats van een tijd of id, geeft 400; zo landt hij nooit in een prompt. */
export const AgendaChatContext = z.strictObject({ from: IsoTime, to: IsoTime, eventId: ItemId.optional() });
export const ChatCreateBody = z.object({
  kind: ChatKind.default('chat'),
  title: z.string().max(CHAT_TITLE_MAX).optional(),
  context: z
    .record(z.string(), z.unknown())
    .superRefine((context, ctx) => {
      for (const [key, value] of Object.entries(context)) {
        if (typeof value === 'string' && value.length > CHAT_CONTEXT_TEXT_MAX) ctx.addIssue({ code: 'custom', message: `context.${key} is langer dan ${CHAT_CONTEXT_TEXT_MAX} tekens`, path: [key] });
      }
    })
    .optional(),
  text: z.string().optional(),
}).superRefine((body, ctx) => {
  // Een agendagesprek neemt alleen de context van AgendaChatContext aan (2.2.54); de andere soorten houden hun grenzen hierboven.
  if (body.kind !== 'agenda' || body.context === undefined) return;
  const r = AgendaChatContext.safeParse(body.context);
  if (!r.success) for (const issue of r.error.issues) ctx.addIssue({ code: 'custom', message: `context: ${issue.message}`, path: ['context', ...issue.path] });
});
export type ChatCreateBody = z.infer<typeof ChatCreateBody>;
export const ChatPatch = z.object({
  title: z.string().optional(),
  modelTier: ModelTierSchema.optional(),
  fast: z.boolean().optional(),
  pinned: z.boolean().optional(),
  archived: z.boolean().optional(),
});
/** Een bericht in een gesprek. `eventId` (sinds 2.2.54, Bespreek bij een afspraak): het agenda-item waar dit bericht over gaat, als
 * gegevens naast de tekst, en alleen als id (`ItemId`); de server zet het alleen als id in de prompt van de beurt, want het onderwerp
 * komt van buiten. */
export const ChatMessageBody = z.object({ text: z.string().trim().min(1).max(20000), eventId: ItemId.optional() });

// ---- Agenda ----

export const AgendaEvent = z.object({
  id: z.string(),
  subject: z.string(),
  start: z.string(),
  end: z.string(),
  isAllDay: z.boolean(),
  location: z.string().nullable(),
  online: z.string().nullable(),
  organizer: MailAddress.nullable(),
  attendees: z.number().int(),
  responseStatus: z.string().nullable(),
  preview: z.string(),
  webLink: z.string().nullable(),
  isSeries: z.boolean(),
});
export type AgendaEvent = z.infer<typeof AgendaEvent>;

/** Deelnemer aan een afspraak, met het antwoord dat de organisator van hem kreeg. */
export const AgendaAttendee = z.object({
  name: z.string(),
  address: z.string(),
  type: z.enum(['required', 'optional', 'resource']),
  response: z.enum(['none', 'organizer', 'tentativelyAccepted', 'accepted', 'declined', 'notResponded']),
});
export type AgendaAttendee = z.infer<typeof AgendaAttendee>;

/** Alles van één afspraak (GET /api/agenda/events/:id), zoals de connector het geeft. */
export const AgendaEventDetail = AgendaEvent.extend({
  bodyText: z.string(),
  attendeesList: z.array(AgendaAttendee),
  /** Jeroen is de organisator: dan geen accepteren/afwijzen, wel bewerken en verwijderen (met afzegging). */
  isOrganizer: z.boolean(),
  seriesMasterId: z.string().nullable(),
  isCancelled: z.boolean(),
  showAs: z.string().nullable(),
  importance: z.enum(['low', 'normal', 'high']),
  categories: z.array(z.string()),
  reminderMinutes: z.number().int().nullable(),
  createdAt: z.string().nullable(),
  modifiedAt: z.string().nullable(),
  /** Korte Nederlandse omschrijving van de herhaling, bv. "elke week op maandag". */
  recurrence: z.string().nullable(),
});
export type AgendaEventDetail = z.infer<typeof AgendaEventDetail>;

export const AgendaResponse = z.enum(['accept', 'tentative', 'decline']);
export type AgendaResponse = z.infer<typeof AgendaResponse>;
export const AgendaRespondBody = z.object({ response: AgendaResponse, comment: z.string().trim().max(2000).optional(), sendResponse: z.boolean().optional() });
export type AgendaRespondBody = z.infer<typeof AgendaRespondBody>;

/** Lokale tijd zonder zone (Europe/Amsterdam), zoals de connector die verwacht: 2026-09-14T08:30:00. */
const LOCAL_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/;
export const AgendaEventPatch = z
  .object({
    subject: z.string().trim().min(1).max(255).optional(),
    start: z.string().regex(LOCAL_TIME, 'start moet een lokale tijd zijn (JJJJ-MM-DDTUU:MM:SS)').optional(),
    end: z.string().regex(LOCAL_TIME, 'end moet een lokale tijd zijn (JJJJ-MM-DDTUU:MM:SS)').optional(),
    allDay: z.boolean().optional(),
    location: z.string().trim().max(255).optional(),
    bodyText: z.string().max(20000).optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), { message: 'Niets om bij te werken.' });
export type AgendaEventPatch = z.infer<typeof AgendaEventPatch>;

// ---- Regels ----

export const OutlookRule = z.object({ id: z.string(), name: z.string(), enabled: z.boolean(), sequence: z.number().int(), summary: z.string() });
export const SmartRule = z.object({ id: z.string(), name: z.string(), description: z.string(), mode: z.string(), enabled: z.boolean(), lastRunAt: z.string().nullable() });
export const SmartDecision = z.object({ id: z.string(), ruleName: z.string(), subject: z.string(), action: z.string(), reason: z.string(), status: z.string(), createdAt: z.string() });
export const RulesOverview = z.object({ rules: z.array(OutlookRule), smartRules: z.array(SmartRule), decisions: z.array(SmartDecision) });
export type RulesOverview = z.infer<typeof RulesOverview>;

// ---- Kennis ----

export const KnowledgeKind = z.enum(['knowledge', 'style', 'preference', 'sign_rule']);
export type KnowledgeKind = z.infer<typeof KnowledgeKind>;
export const Knowledge = z.object({
  id: z.string(),
  kind: KnowledgeKind,
  text: z.string(),
  data: z.record(z.string(), z.unknown()).nullable(),
  source: z.record(z.string(), z.unknown()).nullable(),
  tags: z.array(z.string()),
  active: z.boolean(),
  createdAt: z.string(),
});
export type Knowledge = z.infer<typeof Knowledge>;
/** Hooguit zoveel tekens heeft de tekst van een kennisitem in de API; de kennis in een prompt houdt dezelfde grens (2.2.59). */
export const KNOWLEDGE_TEXT_MAX = 8000;

export const KnowledgeBody = z.object({
  kind: KnowledgeKind.default('knowledge'),
  text: z.string().trim().min(1).max(KNOWLEDGE_TEXT_MAX),
  data: z.record(z.string(), z.unknown()).optional(),
  source: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).default([]),
});
export const KnowledgePatch = z.object({ text: z.string().optional(), tags: z.array(z.string()).optional(), active: z.boolean().optional() });

// ---- Excel ----

/** Een Excel-werkboek (SharePoint of OneDrive) waar Jerome bij kan: via de connector met de Graph-workbook-API. */
/** Regel die de connector bij het versturen van een concept van Jerome onder de handtekening zet. */
export const JEROME_FOOTER = 'Mail samen met Jerome Assistent opgesteld';

export const Workbook = z.object({
  id: z.string().trim().min(1).max(60),
  name: z.string().trim().min(1).max(80),
  url: z.string().trim().min(1),
  /** Wat erin staat en hoe het is ingedeeld; gaat mee in de prompt van Jerome. */
  description: z.string().default(''),
});
export type Workbook = z.infer<typeof Workbook>;

export const CHATEAU_WORKBOOK: Workbook = {
  id: 'chateau-hero',
  name: 'Chateau Hero',
  url: 'https://heroone.sharepoint.com/:x:/r/sites/AllHeroes-Gebouwbeheer/Gedeelde%20documenten/Gebouwbeheer/Hero%20Trois%20Ponts/Chateau%20Hero%20grote%20Excel.xlsx?d=w3652d5243b7942c9991520fd312d4a38&csf=1&web=1&e=KfHbS9',
  description: [
    'Bezetting en beheer van Chateau Hero (Trois-Ponts). Werkbladen:',
    "- 'Bezetting Planning' (A1:K1084): rij 1 een waarschuwing, rij 2 de kopjes, vanaf rij 3 één rij per dag. Kolom A de datum als tekst (bijv. 'di 12 mrt 2024'); rij 3 = 12 maart 2024, dus rij = 3 + aantal dagen sinds 12 maart 2024 (controleer altijd de datumtekst in kolom A). Kolommen: B Check in, C Total, D Hele villa, E Jeroen, F Schoonmaak gepland, G Stephane, H Ludovic (Spark), I Est. Persons, J Dogs, K Note.",
    "- 'Schoonmaakoverzicht' (A1:Q94): periode en tabel met #, Check-in, Check-uit, Nachten, Gast/groep, Personen, Hond, Schoonmaak vóór/na, Notitie.",
    "- 'Offsite Wognum' (A1:X35): kamerindeling van een offsite.",
    "- 'Takenlijst' (A1:J119): VOLG, TAAK, Ruimtenummer, PRIORITEIT, RESOURCE, STATUS, BEGINDATUM, VOLTOOID, OPMERKING (kopjes op rij 4).",
    "- 'Gegevensvalidatie' (statuslijst) en plattegrondbladen (Keldertek, Begane grond, Verdieping, Zolder, Garage, Kavel) zonder celgegevens.",
    'Alleen Jeroen de Vries plant bezetting in (dat staat ook in rij 1 van Bezetting Planning): wijzig nooit op eigen initiatief, alleen op zijn uitdrukkelijke verzoek, en meld daarna precies welke cellen je hebt veranderd.',
  ].join('\n'),
};

export const ExcelSheetInfo = z.object({
  name: z.string(),
  position: z.number().int(),
  visible: z.boolean(),
  address: z.string().nullable(),
  rowCount: z.number().int(),
  columnCount: z.number().int(),
});
export type ExcelSheetInfo = z.infer<typeof ExcelSheetInfo>;

export const ExcelWorkbook = z.object({
  workbook: Workbook,
  name: z.string(),
  webUrl: z.string().nullable(),
  lastModified: z.string().nullable(),
  sheets: z.array(ExcelSheetInfo),
});
export type ExcelWorkbook = z.infer<typeof ExcelWorkbook>;

export const ExcelSheetData = z.object({
  sheet: z.string(),
  address: z.string(),
  rowCount: z.number().int(),
  columnCount: z.number().int(),
  /** Opgemaakte celteksten, rij voor rij. */
  text: z.array(z.array(z.string())),
  truncated: z.boolean(),
});
export type ExcelSheetData = z.infer<typeof ExcelSheetData>;

export const ExcelHit = z.object({ address: z.string(), row: z.number().int(), column: z.number().int(), text: z.string(), rowText: z.string() });
export type ExcelHit = z.infer<typeof ExcelHit>;
export const ExcelFindResult = z.object({ hits: z.array(ExcelHit) });
export type ExcelFindResult = z.infer<typeof ExcelFindResult>;

export const ExcelCellValue = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type ExcelCellValue = z.infer<typeof ExcelCellValue>;
export const ExcelRangeBody = z.object({
  sheet: z.string().min(1),
  /** Celadres of bereik, bijvoorbeeld B12 of B12:D12. */
  address: z.string().trim().min(1).max(40),
  values: z.array(z.array(ExcelCellValue)).min(1),
});
export type ExcelRangeBody = z.infer<typeof ExcelRangeBody>;
export const ExcelRangeResult = z.object({ address: z.string(), text: z.array(z.array(z.string())) });
export type ExcelRangeResult = z.infer<typeof ExcelRangeResult>;

/** Opmaak van een cel in de weergave van een blad (index in `ExcelView.styles`; index 0 is altijd leeg). */
export const ExcelStyle = z.object({
  /** Vulling en tekstkleur als '#rrggbb'. */
  bg: z.string().optional(),
  fg: z.string().optional(),
  /** Vet, cursief, onderstreept, doorgestreept. */
  b: z.literal(1).optional(),
  i: z.literal(1).optional(),
  u: z.literal(1).optional(),
  st: z.literal(1).optional(),
  /** Lettergrootte in punten. */
  sz: z.number().optional(),
  /** Uitlijning: horizontaal (links, midden, rechts) en verticaal (boven, midden, onder). */
  al: z.enum(['l', 'c', 'r']).optional(),
  va: z.enum(['t', 'm', 'b']).optional(),
  wrap: z.literal(1).optional(),
  /** Randen als letters l, r, t en b. */
  bd: z.string().optional(),
});
export type ExcelStyle = z.infer<typeof ExcelStyle>;
/** Zichtbare kolom: echte kolomindex (1-gebaseerd), letter en breedte in px. */
export const ExcelViewCol = z.object({ c: z.number().int(), letter: z.string(), w: z.number() });
export type ExcelViewCol = z.infer<typeof ExcelViewCol>;
/** Cel zoals Excel hem toont: tekst, stijlindex (weggelaten = geen opmaak) en databalk 0..100. */
export const ExcelViewCell = z.object({ t: z.string(), s: z.number().int().optional(), bar: z.number().optional() });
export type ExcelViewCell = z.infer<typeof ExcelViewCell>;
/** Zichtbare rij: echt rijnummer, hoogte in px en één cel per zichtbare kolom. */
export const ExcelViewRow = z.object({ r: z.number().int(), h: z.number(), cells: z.array(ExcelViewCell) });
export type ExcelViewRow = z.infer<typeof ExcelViewRow>;
/** Afbeelding op een blad (plattegrond): positie en maat in px ten opzichte van de linkerbovenhoek van het zichtbare raster. */
export const ExcelImage = z.object({ i: z.number().int(), x: z.number(), y: z.number(), w: z.number(), h: z.number(), name: z.string() });
export type ExcelImage = z.infer<typeof ExcelImage>;
/** Een blad zoals Excel het toont: alleen zichtbare rijen en kolommen (met hun echte nummers), opmaak, blokkering, samengevoegde cellen en afbeeldingen. */
export const ExcelView = z.object({
  sheet: z.string(),
  /** Gebruikt bereik, bijvoorbeeld A1:K1084. */
  address: z.string(),
  cols: z.array(ExcelViewCol),
  rows: z.array(ExcelViewRow),
  styles: z.array(ExcelStyle),
  /** Bevroren rijen en kolommen, geteld in zichtbare rijen en kolommen vanaf linksboven. */
  freeze: z.object({ rows: z.number().int(), cols: z.number().int() }),
  /** Samengevoegde cellen als echte adressen ('A1:C1'); kunnen deels op verborgen rijen of kolommen liggen. */
  merges: z.array(z.string()),
  images: z.array(ExcelImage),
  /** Aantal verborgen rijen en kolommen binnen het bereik. */
  hiddenRows: z.number().int(),
  hiddenCols: z.number().int(),
  /** False als de opmaak niet beschikbaar was: dan alleen tekst met standaardbreedtes. */
  formatted: z.boolean(),
  truncated: z.boolean(),
  modified: z.string().nullable(),
});
export type ExcelView = z.infer<typeof ExcelView>;

// ---- Instellingen ----

export const QuickInstruction = z.object({ label: z.string(), text: z.string(), key: z.string().max(1).optional() });
export type QuickInstruction = z.infer<typeof QuickInstruction>;

/** Eén instelbare breedte (layout.ts): een getal, binnen de grenzen gezet (ook een bewaarde waarde van vroeger, zodat een
 * gewijzigde grens de rest van de instellingen niet terugzet); geen getal is een fout (400 bij PUT /api/settings). */
const layoutValue = (key: LayoutKey) => z.number().transform((v) => clampLayout(key, v)).default(LAYOUT_LIMITS[key].default);

/** Waar het leesvenster op het tabblad Mail staat (desktop, ticket 28625 deel 2): rechts van de lijst, of eronder zoals in Outlook. */
export const ReaderPosition = z.enum(['right', 'below']);
export type ReaderPosition = z.infer<typeof ReaderPosition>;

/** Een mailadres zoals de meldingsmail het als ontvanger neemt (server/src/watch/notify.ts) en het instellingenscherm het bewaart:
 * iets@domein.tld, zonder spaties; witruimte eromheen telt niet. */
export function isMailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export const Settings = z.object({
  models: z
    .object({
      chat: ModelTierSchema.default('standaard'),
      mail: ModelTierSchema.default('standaard'),
      chatFast: z.boolean().default(false),
      allowLight: z.boolean().default(false),
    })
    .default({ chat: 'standaard', mail: 'standaard', chatFast: false, allowLight: false }),
  maxWorkers: z.number().int().min(0).max(64).default(0),
  budgets: z.object({ mail: z.number().default(3), research: z.number().default(10), dev: z.number().default(10), chat: z.number().default(5) }).default({ mail: 3, research: 10, dev: 10, chat: 5 }),
  quickInstructions: z
    .array(QuickInstruction)
    .default([
      { label: 'Beantwoord', text: 'Beantwoord deze mail: ', key: '1' },
      { label: 'Vat samen', text: 'Vat deze mail en de bijlagen samen', key: '2' },
      { label: 'Onderteken', text: 'Onderteken en parafeer de bijlagen en zet een antwoord klaar met het getekende exemplaar', key: '3' },
      { label: 'Plan in', text: 'Plan dit in mijn agenda: ', key: '4' },
      { label: 'Zoek uit', text: 'Zoek uit: ', key: '5' },
      { label: 'Sla kennis op', text: 'Sla de kern van deze mail op als kennis', key: '6' },
    ]),
  askAlwaysTools: z.array(z.string()).default([]),
  finish: z.object({ unflag: z.boolean().default(true), markRead: z.boolean().default(true) }).default({ unflag: true, markRead: true }),
  /** Map-ids, of `wk:<well-known naam>` voor een vaste map (standaard de map Concepten). */
  favoriteFolders: z.array(z.string()).default(['wk:drafts']),
  workersRail: z.object({ visible: z.boolean().default(true), showAll: z.boolean().default(false) }).default({ visible: true, showAll: false }),
  /** De chat: de gesprekkenlijst links uitgeklapt (desktop; anders de strook van 44 px) en de stappen van Jerome (toolregels, gevonden
   * mails en mislukte tools) in het gesprek uitgeklapt; standaard staat elke groep stappen als één regel (ticket 28075). Een bewaarde
   * `stepsVisible` van vóór ticket 28075 valt bij het lezen weg, zodat iedereen de stappen eerst ingeklapt ziet. */
  chat: z.object({ listVisible: z.boolean().default(true), stepsExpanded: z.boolean().default(false) }).default({ listVisible: true, stepsExpanded: false }),
  /** Het tabblad Mail op de desktop (ticket 28625): de maillijst naast de lezer zichtbaar, of ingeklapt tot een strook van 44 px
   * zodat de lezer de rest krijgt (de knop Lezer vergroten en het menu Weergave). Zonder open mail staat de lijst er altijd.
   * `readerPosition` (deel 2): het leesvenster rechts van de lijst of eronder, zoals in Outlook (het menu Weergave).
   * Oude instellingen zonder `mail` (of zonder een van de velden) krijgen de standaard; de client stuurt altijd de hele `mail`. */
  mail: z.object({ listVisible: z.boolean().default(true), readerPosition: ReaderPosition.default('right') }).default({ listVisible: true, readerPosition: 'right' }),
  /** De kolom Gevlagd helemaal links (desktop, ticket 30961): uitgeklapt met de lijst van alle gevlagde mails, of ingeklapt tot een
   * strook van 44 px met het aantal. Standaard uitgeklapt, zoals de gesprekkenlijst en de werkerskolom; op de telefoon is de lijst
   * een vel vanuit de bovenbalk en telt de instelling niet. */
  flagged: z.object({ visible: z.boolean().default(true) }).default({ visible: true }),
  /** De breedtes van de desktop die de gebruiker zelf instelt door de rand ertussen te slepen (ticket 28376, layout.ts): de
   * gesprekkenkolom, de kolom Gevlagd (ticket 30961), de werkerskolom, het mailpaneel naast het gesprek en de maillijst op het tabblad Mail (ticket 28625) in px,
   * de verdeling tussen de twee panelen als het deel voor het linkerpaneel, en met het leesvenster onder de lijst het deel van
   * de hoogte voor de lijst (`mailSplit`, ticket 28625 deel 2), en in het mailpaneel van de chat het deel van zijn hoogte voor de
   * gevonden mails (`mailPaneSplit`, de aanvulling op dat ticket). Oude instellingen zonder `layout` (of zonder een
   * van de breedtes) krijgen de standaard; de client stuurt altijd de hele `layout`. */
  layout: z.object({ chatColumn: layoutValue('chatColumn'), flaggedColumn: layoutValue('flaggedColumn'), workersRail: layoutValue('workersRail'), mailPane: layoutValue('mailPane'), mailList: layoutValue('mailList'), paneSplit: layoutValue('paneSplit'), mailSplit: layoutValue('mailSplit'), mailPaneSplit: layoutValue('mailPaneSplit') }).default(defaultLayout),
  voice: z
    .object({
      voiceId: z.string().default(''),
      speed: z.number().min(0.7).max(1.3).default(1),
      confirmSpoken: z.boolean().default(true),
      vocabulary: z.array(z.string()).default([]),
    })
    .default({ voiceId: '', speed: 1, confirmSpoken: true, vocabulary: [] }),
  /** Ochtendbriefing: standaard om 06:00, zonder mail; het vinkje "Ook per mail" op het instellingenscherm zet de mail aan. */
  briefing: z.object({ enabled: z.boolean().default(true), time: z.string().default('06:00'), mail: z.boolean().default(false) }).default({ enabled: true, time: '06:00', mail: false }),
  /** Einde-dag-samenvatting: kennis uit de mails van vandaag, standaard om 18:00 en zonder mail (tot en met 2.2.56 per mail; de eigenaar
   * wil geen dagmails, 1 november 2026). Het vinkje "Ook per mail" op het instellingenscherm zet de mail aan; een bewaarde rij van
   * vóór 2.2.57 leest als uit (server/src/settings.ts, `fromStored`). */
  eod: z.object({ enabled: z.boolean().default(true), time: z.string().default('18:00'), mail: z.boolean().default(false) }).default({ enabled: true, time: '18:00', mail: false }),
  watch: z.object({ intervalMinutes: z.number().int().min(2).max(120).default(10), autoSign: z.boolean().default(true) }).default({ intervalMinutes: 10, autoSign: true }),
  /** De meldingsmail (ticket 28824, server/src/watch/notify.ts): een mail als een vraag van Jerome twee uur op antwoord wacht, naar
   * `address` of, als dat leeg is, naar het adres van de eigenaar; standaard aan. Een bewaarde rij van vóór 28824 leest als aan
   * (server/src/settings.ts, `fromStored`), want de server las de instelling toen niet en mailde altijd. */
  notifyMail: z.object({ enabled: z.boolean().default(true), address: z.string().default('') }).default({ enabled: true, address: '' }),
  defaultSort: MailSort.default('newest'),
  prefetch: z.number().int().min(0).max(10).default(3),
  /** Excel-werkboeken waar Jerome bij kan (knop bovenin, tabblad, en in de prompt van de agent). */
  workbooks: z.array(Workbook).default([CHATEAU_WORKBOOK]),
});
export type Settings = z.infer<typeof Settings>;
/** De instelbare breedtes van de desktop (ticket 28376). */
export type Layout = Settings['layout'];
export const SettingsPatch = Settings.partial();
export type SettingsPatch = z.infer<typeof SettingsPatch>;
export const defaultSettings = (): Settings => Settings.parse({});

// ---- Overig ----

export const UsageDay = z.object({ day: z.string(), kind: z.string(), tasks: z.number().int(), costUsd: z.number(), inputTokens: z.number(), outputTokens: z.number() });
export type UsageDay = z.infer<typeof UsageDay>;

export const ErrorStatus = z.enum(['open', 'fixing', 'fixed', 'ignored']);
export type ErrorStatus = z.infer<typeof ErrorStatus>;
export const ErrorItem = z.object({
  id: z.string(),
  fingerprint: z.string(),
  message: z.string(),
  stack: z.string().nullable(),
  context: z.record(z.string(), z.unknown()).nullable(),
  count: z.number().int(),
  firstAt: z.string(),
  lastAt: z.string(),
  status: ErrorStatus,
  devChatId: z.string().nullable(),
});
export type ErrorItem = z.infer<typeof ErrorItem>;
export const ClientErrorBody = z.object({ message: z.string().max(2000), stack: z.string().max(20000).optional(), context: z.record(z.string(), z.unknown()).optional() });

export const VersionItem = z.object({ version: z.string(), releasedAt: z.string(), author: z.string(), summary: z.string(), items: z.array(z.string()), commitSha: z.string().nullable() });
export type VersionItem = z.infer<typeof VersionItem>;

export const BriefingKind = z.enum(['morning', 'eod']);
export type BriefingKind = z.infer<typeof BriefingKind>;
export const Briefing = z.object({ id: z.string(), kind: BriefingKind, title: z.string(), day: z.string(), content: z.string(), chatId: z.string().nullable(), mailed: z.boolean(), createdAt: z.string() });
export type Briefing = z.infer<typeof Briefing>;

// ---- Wensen en fouten, signalen, recente acties ----

export const WishKind = z.enum(['wish', 'bug']);
export type WishKind = z.infer<typeof WishKind>;
export const WishStatus = z.enum(['open', 'planned', 'done', 'dismissed']);
export type WishStatus = z.infer<typeof WishStatus>;
export const WishSource = z.enum(['jeroen', 'auto', 'error', 'jerome']);
export type WishSource = z.infer<typeof WishSource>;
export const WishEvidence = z.object({ at: z.string(), text: z.string(), ref: z.string().optional() });
export type WishEvidence = z.infer<typeof WishEvidence>;
export const Wish = z.object({
  id: z.string(),
  kind: WishKind,
  title: z.string(),
  details: z.string(),
  status: WishStatus,
  source: WishSource,
  /** 1 = hoog, 2 = normaal, 3 = laag. */
  priority: z.number().int(),
  /** Hoe vaak dit is gesignaleerd. */
  count: z.number().int(),
  fingerprint: z.string().nullable(),
  evidence: z.array(WishEvidence),
  firstAt: z.string(),
  lastAt: z.string(),
  doneVersion: z.string().nullable(),
  devChatId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Wish = z.infer<typeof Wish>;
export const WishBody = z.object({ kind: WishKind.default('wish'), title: z.string().trim().min(1).max(200), details: z.string().max(4000).default(''), priority: z.number().int().min(1).max(3).default(2) });
export type WishBody = z.infer<typeof WishBody>;
export const WishPatch = z.object({ kind: WishKind.optional(), title: z.string().trim().min(1).max(200).optional(), details: z.string().max(4000).optional(), status: WishStatus.optional(), priority: z.number().int().min(1).max(3).optional() });
export type WishPatch = z.infer<typeof WishPatch>;

/** Signalen uit het gebruik van de app; de dagelijkse digest maakt er wensen en fouten van. */
export const SignalKind = z.enum(['client_error', 'api_error', 'slow', 'rage_click', 'undo', 'task_failed', 'task_steered', 'task_retry', 'sign_retry', 'note']);
export type SignalKind = z.infer<typeof SignalKind>;
export const Signal = z.object({ id: z.string(), kind: SignalKind, text: z.string(), data: z.record(z.string(), z.unknown()).nullable(), at: z.string(), processedAt: z.string().nullable() });
export type Signal = z.infer<typeof Signal>;
export const SignalBody = z.object({ kind: SignalKind, text: z.string().trim().min(1).max(1000), data: z.record(z.string(), z.unknown()).optional() });
export type SignalBody = z.infer<typeof SignalBody>;
export const SignalsBody = z.object({ signals: z.array(SignalBody).min(1).max(50) });
export type SignalsBody = z.infer<typeof SignalsBody>;

export const ActionKind = z.enum(['flag', 'unflag', 'read', 'unread', 'archive', 'delete', 'move', 'finish', 'eod', 'eod_remove', 'send']);
export type ActionKind = z.infer<typeof ActionKind>;
export const ActionItem = z.object({
  id: z.string(),
  kind: ActionKind,
  /** De mail waarop de actie is gedaan (id op dat moment). */
  messageId: z.string(),
  /** Het huidige id van die mail (na verplaatsen, archiveren of verwijderen krijgt een mail een nieuw id). */
  currentId: z.string(),
  subject: z.string(),
  fromName: z.string().nullable(),
  source: z.enum(['jeroen', 'jerome']),
  data: z.record(z.string(), z.unknown()).nullable(),
  at: z.string(),
  undoneAt: z.string().nullable(),
});
export type ActionItem = z.infer<typeof ActionItem>;

export const ChangelogEntry = z.object({ version: z.string(), date: z.string(), author: z.string(), items: z.array(z.string()) });
export type ChangelogEntry = z.infer<typeof ChangelogEntry>;

export const Me = z.object({ name: z.string(), email: z.string(), version: z.string(), settings: Settings, stub: z.boolean() });
export type Me = z.infer<typeof Me>;

// ---- Websocket ----

export const PermissionItem = z.object({
  id: z.string(),
  taskId: z.string().nullable(),
  chatId: z.string().nullable(),
  tool: z.string(),
  inputSummary: z.string(),
  status: z.enum(['pending', 'allowed', 'denied']),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type PermissionItem = z.infer<typeof PermissionItem>;

export const MailActionResult = z.object({ ok: z.literal(true), id: z.string(), previousFolderId: z.string().nullable() });
export type MailActionResult = z.infer<typeof MailActionResult>;

export const TaskListQuery = z.object({
  status: z.string().optional(),
  day: z.string().optional(),
  /** Bijgewerkt of bekeken op of na dit moment (ISO-tijd), voor de bekeken taken van de werkerskolom (2.3.10); niet samen met `day`. */
  since: z.string().optional(),
  /** `1`: alleen taken zonder `seenAt` (de klare taken die de eigenaar nog niet opende, 2.3.10). */
  unseen: z.literal('1').optional(),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type TaskListQuery = z.infer<typeof TaskListQuery>;

/** Uitkomst van het leren na het versturen van een concept van Jerome. */
export const DraftLearned = z.object({
  draftId: z.string(),
  taskId: z.string().nullable(),
  messageId: z.string().nullable(),
  subject: z.string(),
  /** Verstuurd met de knop in Jerome of (door de bewaking herkend) via Outlook. */
  via: z.enum(['jerome', 'outlook']),
  /** Jeroen verstuurde de tekst van Jerome ongewijzigd. */
  unchanged: z.boolean(),
  lessons: z.array(Knowledge),
  at: z.string(),
});
export type DraftLearned = z.infer<typeof DraftLearned>;

/** Wat er met een mail of afspraak gebeurde, zoals de stroom van de maildienst het meldt (mailbox-mcp 0.1.9, 2.3.2). */
export const StroomChange = z.enum(['created', 'updated', 'deleted']);
export type StroomChange = z.infer<typeof StroomChange>;

/**
 * De stand van de stroom van de maildienst (2.3.2, server/src/connector/stroom.ts): `connected` (mail en agenda komen als melding;
 * het tabblad Mail ververst dan alleen als vangnet elke tien minuten), `disconnected` (de server verbindt opnieuw; de client peilt
 * elke minuut, zoals vóór 2.3.2) of `fallback` (de dienst weigert de stroom met 401 of 404; peilen tot een herstart van Jerome 2).
 */
export const StroomStatus = z.enum(['connected', 'disconnected', 'fallback']);
export type StroomStatus = z.infer<typeof StroomStatus>;

export const WsEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('hello'), version: z.string(), instance: z.string() }),
  z.object({ type: z.literal('task.updated'), task: Task }),
  z.object({ type: z.literal('task.event'), event: TaskEvent }),
  z.object({ type: z.literal('counts'), counts: Counts }),
  z.object({ type: z.literal('chat.delta'), chatId: z.string(), messageId: z.string(), text: z.string() }),
  z.object({ type: z.literal('chat.message'), message: Message }),
  // Een wachtend bericht is uit de wachtrij gehaald (kruisje of Stop) en bestaat niet meer.
  z.object({ type: z.literal('chat.message.removed'), chatId: z.string(), messageId: z.string() }),
  z.object({ type: z.literal('chat.status'), chatId: z.string(), status: z.enum(['idle', 'running', 'error']) }),
  // Een gesprek veranderde buiten een beurt om: de titel uit het model of de kosten daarvan (server/src/chat/title.ts). De client
  // neemt het gesprek over behalve de status; die komt alleen met chat.status, want die melding kan op deze voorlopen.
  z.object({ type: z.literal('chat.updated'), chat: Chat }),
  // Mail veranderde: de ids (leeg: alles verversen). Uit de stroom van de maildienst (2.3.2) ook wat er veranderde en de map waar het
  // bericht nu staat (`bekendeMap` alleen bij een bekende map van Graph); de bewaking, acties en concepten sturen alleen de ids.
  z.object({ type: z.literal('mail.changed'), ids: z.array(z.string()), wijziging: StroomChange.optional(), mapId: z.string().nullable().optional(), bekendeMap: z.string().optional() }),
  // Een afspraak veranderde (de stroom van de maildienst, 2.3.2): de ids (leeg: alles verversen); de agenda laadt het zichtbare bereik.
  z.object({ type: z.literal('agenda.changed'), ids: z.array(z.string()) }),
  // De stand van de stroom van de maildienst (2.3.2): bij elke wijziging en direct na de hello, zodat de client weet of hij moet peilen.
  z.object({ type: z.literal('stroom.status'), status: StroomStatus }),
  z.object({ type: z.literal('briefing'), briefing: Briefing }),
  z.object({ type: z.literal('error'), error: ErrorItem }),
  z.object({ type: z.literal('dev.status'), chatId: z.string(), status: z.string(), text: z.string() }),
  z.object({ type: z.literal('settings'), settings: Settings }),
  z.object({ type: z.literal('permission'), permission: PermissionItem }),
  z.object({ type: z.literal('wish'), wish: Wish }),
  z.object({ type: z.literal('action'), action: ActionItem }),
  z.object({ type: z.literal('draft.learned'), learned: DraftLearned }),
  // De stand van de WhatsApp-koppeling veranderde (de bewaker in server/src/whatsapp/watcher.ts, sinds 2.3.1, elke 15 s en direct
  // na een opdracht).
  z.object({ type: z.literal('whatsapp.status'), status: WaStatus }),
  // Een melding over WhatsApp (sinds 2.3.1): ontkoppeld, geblokkeerd of gestopt; precies één per voorval (server/src/watch/notify.ts).
  z.object({ type: z.literal('whatsapp.alert'), alert: WaAlert }),
  // Hartslag van de server, elke 25 s (server/src/realtime/hub.ts): houdt de verbinding open; blijft hij uit, dan is ze dood.
  z.object({ type: z.literal('ping') }),
]);
export type WsEvent = z.infer<typeof WsEvent>;
